import { useCallback, useEffect, useState } from "react";
import axios, { AxiosRequestHeaders } from "axios";

import { ScheduleSettings,ParameterDefinition } from "./useParameters";
import { useMessage } from "./useMessage";
import { useLoginUser } from "../hooks/useLoginUser";
import appconfig from "../config.json";

// DescribeRuleCommandOutput の型定義
interface DescribeRule {
  Name?: string;
  Arn?: string;
  EventPattern?: string;
  ScheduleExpression?: string;
  State?: RuleState | string;
  Description?: string;
  RoleArn?: string;
  ManagedBy?: string;
  EventBusName?: string;
}
// ParameterWithValue interface extends ParameterDefinition
interface ParameterWithValue extends ParameterDefinition {
  value?: string;
}
// RuleState の型は以下のいずれか
type RuleState = "DISABLED" | "ENABLED" | "ENABLED_WITH_ALL_CLOUDTRAIL_MANAGEMENT_EVENTS" | string;

// ルールステート更新レスポンス型
interface UpdateRuleStateResponse {
  success: boolean;
  message?: string;
}

export const useTenantJob = (onClose: () => void) => {
  const { showMessage } = useMessage();
  const { loginUser, tokenRefresh, selectTenant } = useLoginUser();
  const [loading, setLoading] = useState(false);
  const [parameters, setParameters] = useState<ParameterWithValue[]>([]);
  const [loadingDescribeRule, setLoadingDescribeRule] = useState(false);
  const [loadingParamters, setLoadingParamters] = useState(false);
  const [ruleTargets, setRuleTargets] = useState<any[]>([]);
  
  // Monitor loadingDescribeRule changes
  useEffect(() => {
    if (!loadingDescribeRule && !loadingParamters) {
      setLoading(false);
    }
  }, [loadingDescribeRule,loadingParamters]);
  const [describeRule, setDescribeRule] = useState<DescribeRule | undefined>(
    undefined
  );
  const API_MANAGER_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];

  type IPostResponse = {
    access?: string;
    token?: string;
    newPasswordRequired?: string;
  };

  const getScheduleSettingDetail = ((tenantJob: ScheduleSettings) => {
    if (tenantJob) {
      setLoading(true);
      // manualの場合はEventBridgeを取得しない
      if (tenantJob.eventsRuleName && tenantJob.eventsRuleName !== "manual") {
        getEnentBrige(tenantJob.eventsRuleName);
        getRuleTargets(tenantJob.eventsRuleName);
      } else {
        // manualの場合は即座に完了とする
        setLoadingDescribeRule(false);
        setDescribeRule(undefined);
        setRuleTargets([]);
      }
      getParameters(tenantJob.parameters || [])
    }
  })
  const getEnentBrige = useCallback((name: string) => {
    setLoadingDescribeRule(true);
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get<DescribeRule>(API_MANAGER_URL + "/eventbridge/" + name, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data) {
            setDescribeRule(res.data);
          } else {
            setDescribeRule(undefined);
          }
        })
        .catch(() =>
          showMessage({
            title: "DescribeRuleの取得に失敗しました",
            status: "error",
          })
        )
        .finally(() => setLoadingDescribeRule(false));
      });
  }, []);

  const getParameters = useCallback((parameterDefs: ParameterDefinition[]) => {
    setLoadingParamters(true);
    
    // Guard against undefined parameterDefs
    if (!parameterDefs || !Array.isArray(parameterDefs)) {
      setParameters([]);
      setLoadingParamters(false);
      return;
    }
    
    // Set initial parameters
    const initialParameters = parameterDefs.map(param => ({
      ...param,
      value: undefined
    }));
    setParameters(initialParameters);
    
    // Fetch each parameter's value
    const parameterPromises = parameterDefs.map(param => {
      const path = param.path;
      return new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        return axios
          .get<{ Value?: string }>(API_MANAGER_URL + `/parameter?path=${path}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            return {
              ...param,
              value: res.data.Value
            };
          })
          .catch(() => {
            showMessage({
              title: "パラメータの取得に失敗しました",
              status: "error",
            });
            return {
              ...param,
              value: undefined
            };
          });
      });
    });
    
    Promise.all(parameterPromises)
      .then(updatedParameters => {
        setParameters(updatedParameters);
      })
      .finally(() => {
        setLoadingParamters(false);
      });
  }, []);
  // ルールの状態を更新する関数
  const updateRuleState = useCallback((name: string, newState: RuleState) => {
    setLoadingDescribeRule(true);
    let atr: string = "";
    if (newState) {
      if (newState=="ENABLED"){
        atr = "/state?action=enable";  
      }else{
        atr = "/state?action=disable";
      }
    }
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .put<DescribeRule>(API_MANAGER_URL + "/eventbridge/" + name + atr,{}, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (res.data) {
            setDescribeRule(res.data);
          } else {
            setDescribeRule(undefined);
          }
        })
        .catch(() =>
          showMessage({
            title: "DescribeRuleの取得に失敗しました",
            status: "error",
          })
        )
        .finally(() => setLoadingDescribeRule(false));
      });
  }, []);

  // ルールのスケジュール式を更新する関数
  const updateSchedule = useCallback((name: string, scheduleExpression: string) => {
    setLoadingDescribeRule(true);
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .put<DescribeRule>(API_MANAGER_URL + "/eventbridge/" + name + "/schedule", 
          { scheduleExpression },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          if (res.data) {
            setDescribeRule(res.data);
            showMessage({ title: "スケジュールを更新しました", status: "success" });
          }
        })
        .catch(() =>
          showMessage({ title: "スケジュールの更新に失敗しました", status: "error" })
        )
        .finally(() => setLoadingDescribeRule(false));
    });
  }, []);

  const putParameter = useCallback((path: string, value: string) => {
    if (path) {
      type Param = {
        Value: string;
      };

      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        axios
          .put<Param>(API_MANAGER_URL + `/parameter?path=${path}`, { value: value }, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          })
          .then((res) => {
              console.log(res.data);
              showMessage({
          title: "パラメータを設定しました",
          status: "success",
              });
          })
          .catch(() => {
            showMessage({
              title: "パラメータの設定に失敗しました",
              status: "error",
            });
          });
      });
    }
  }, []);

  const getRuleTargets = useCallback((name: string) => {
    if (!name) {
      setRuleTargets([]);
      return;
    }
    
    console.log('getRuleTargets called with name:', name);
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get(`${API_MANAGER_URL}/eventbridge/${name}/targets`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          console.log('Targets response:', res.data);
          setRuleTargets(res.data.Targets || []);
        })
        .catch((error) => {
          console.error('Error fetching targets:', error);
          setRuleTargets([]);
        });
    });
  }, []);

  const updateRuleTarget = useCallback((ruleName: string, targetId: string, input: any) => {
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .put(`${API_MANAGER_URL}/eventbridge/${ruleName}/targets`, 
          { targetId, input },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then(() => {
          showMessage({ title: "実行パラメータを保存しました", status: "success" });
        })
        .catch(() => {
          showMessage({ title: "実行パラメータの保存に失敗しました", status: "error" });
        });
    });
  }, []);

  return {
    getScheduleSettingDetail,
    updateRuleState,
    updateSchedule,
    updateRuleTarget,
    putParameter,
    loading,
    loadingDescribeRule,
    describeRule,
    setParameters,
    parameters,
    ruleTargets
  };
};
